/*
 * Vendored from CopilotKit (https://github.com/CopilotKit/CopilotKit)
 * packages/channels-slack/src/chunked-message-stream.ts @ 66fef88b249e9cac2d76a238678c19d3ce6b2913.
 * Copyright (c) Atai Barkai — MIT License.
 * Adapted for @skaile/workspaces/channels: platform-neutral naming and
 * parametric limits; Slack SDK imports removed. See NOTICE at package root.
 */
import {
  autoCloseMarkdown,
  detectOpenContext,
  renderContextOpener,
} from "./auto-close-streaming.js";
import { SerializedMessageStream } from "./message-stream.js";

/**
 * Position (0-based) of the unpaired opening ``` in `buffer.slice(0, end)`,
 * or null if all triple-backticks before `end` are paired.
 */
function findUnpairedFenceStart(buffer: string, end: number): number | null {
  const before = buffer.slice(0, end);
  const parts = before.split("```");
  // length is odd → balanced; even → one unpaired opener exists
  if (parts.length % 2 !== 0) return null;
  return before.lastIndexOf("```");
}

/**
 * Options for {@link ChunkedMessageStream}.
 */
export interface ChunkedMessageStreamOptions {
  /** Per-message character limit (e.g. ~4000 for Slack, ~28k for Teams). */
  maxLength: number;
  /** Posts a new platform message for chunk `index` with initial `chunk` text. */
  post: (chunk: string, index: number) => Promise<void>;
  /** Updates the already-posted message for chunk `index` with `chunk` text. */
  update: (chunk: string, index: number) => Promise<void>;
  /** Throttle floor for each underlying stream's update, in ms (default 800). */
  throttleMs?: number;
  /** Forwarded to each per-chunk stream — sink for a failed chunk update. */
  onApplyError?: (err: unknown) => void;
}

const DEFAULT_THROTTLE_MS = 800;

/**
 * Chat platforms cap message length. For longer agent responses we spread the
 * text over several platform messages — but the *chunk boundaries* must be
 * stable once committed (we can't reflow a sentence into a previous chunk
 * after that chunk has already been posted).
 *
 * ChunkedMessageStream owns N underlying {@link SerializedMessageStream}s (one
 * per platform message). It accepts the full accumulated text on `write()`,
 * decides where the chunk boundaries are, mints new messages as needed, and
 * dispatches the right slice to each chunk's stream. Each chunk is passed
 * through {@link autoCloseMarkdown} before it hits the platform, so a split
 * inside a code fence closes the fence at the boundary and reopens it (same
 * info string) at the start of the next chunk.
 *
 * Boundaries are chosen greedily: once an active chunk exceeds `maxLength`,
 * freeze the boundary at the last newline (or last space) within the limit;
 * remaining text becomes the next chunk. Boundaries don't move once frozen,
 * so an already-posted chunk's text never shrinks.
 */
export class ChunkedMessageStream {
  private buffer = "";
  /** Sorted positions where a chunk ends (= where the next chunk begins). */
  private boundaries: number[] = [];
  private streams: SerializedMessageStream[] = [];
  /** Serialises new-chunk creation so async post calls don't race. */
  private setupPromise: Promise<void> = Promise.resolve();
  private finished = false;

  private readonly maxLength: number;
  private readonly throttleMs: number;
  private readonly post: (chunk: string, index: number) => Promise<void>;
  private readonly update: (chunk: string, index: number) => Promise<void>;
  private readonly onApplyError?: (err: unknown) => void;

  constructor(opts: ChunkedMessageStreamOptions) {
    this.maxLength = opts.maxLength;
    this.throttleMs = opts.throttleMs ?? DEFAULT_THROTTLE_MS;
    this.post = opts.post;
    this.update = opts.update;
    this.onApplyError = opts.onApplyError;
  }

  /**
   * Feed the full accumulated text (not a delta). Resolves once any newly
   * required messages have been posted and slices dispatched to their streams
   * (per-chunk updates may still be throttled until {@link finalize}).
   */
  async write(fullText: string): Promise<void> {
    if (this.finished) return;
    if (fullText === this.buffer) return;
    this.buffer = fullText;
    this.refreezeBoundaries();
    // Make sure we have one platform message per chunk, then dispatch.
    this.setupPromise = this.setupPromise.then(() => this.ensureStreamsAndDispatch());
    await this.setupPromise;
  }

  /**
   * Mark the stream done: drain pending setup, run a final dispatch, then
   * force-flush every chunk so all messages reflect the final buffer.
   */
  async finalize(): Promise<void> {
    this.finished = true;
    this.setupPromise = this.setupPromise.then(() => this.ensureStreamsAndDispatch());
    await this.setupPromise;
    for (const s of this.streams) await s.flush();
  }

  /** Returns the number of platform messages this stream has posted so far. */
  get chunkCount(): number {
    return this.streams.length;
  }

  /**
   * Walk forward from the last frozen boundary, freezing new ones whenever
   * the active chunk's length exceeds `maxLength`. Once frozen, a boundary
   * doesn't move.
   *
   * Special case (block-keeps-whole): if the chosen boundary lands INSIDE
   * an open fenced code block, we try to move the boundary BACK to the
   * position right before the fence opener, so the *whole* block lives in
   * the next message rather than being split. The previous chunk gets
   * shortened (update will edit it down). Fallback when the block is too
   * large to fit in one chunk: keep the inside-fence boundary; the
   * dispatcher will prepend the fence opener to the next chunk (re-opener
   * path).
   */
  private refreezeBoundaries(): void {
    let lastFrozen = this.boundaries.at(-1) ?? 0;
    while (this.buffer.length - lastFrozen > this.maxLength) {
      const window = this.buffer.slice(lastFrozen, lastFrozen + this.maxLength);
      let breakAt = window.lastIndexOf("\n");
      if (breakAt < this.maxLength / 4) breakAt = window.lastIndexOf(" ");
      if (breakAt < 0) breakAt = this.maxLength - 1;
      let candidate = lastFrozen + breakAt + 1;

      // Block-keeps-whole: move boundary back if it lands inside an open
      // fence. Only adjust if the fence opener is in the latter ~70% of
      // the active chunk — otherwise shrinking would lose too much
      // content (chunk N would be tiny) and the re-opener path is better.
      const fenceStart = findUnpairedFenceStart(this.buffer, candidate);
      if (fenceStart !== null) {
        const minAcceptable = lastFrozen + Math.floor(this.maxLength * 0.3);
        if (fenceStart > minAcceptable) {
          candidate = fenceStart;
        }
      }

      this.boundaries.push(candidate);
      lastFrozen = candidate;
    }
  }

  private async ensureStreamsAndDispatch(): Promise<void> {
    // If we've never received any content, don't post anything — a stream
    // that opens and ends with no content should produce no message.
    if (this.buffer.length === 0) return;
    const chunkCount = this.boundaries.length + 1;
    while (this.streams.length < chunkCount) {
      const i = this.streams.length;
      const initialText =
        i === 0
          ? autoCloseMarkdown(this.buffer.slice(0, this.boundaries[0])) || " "
          : "_…(continued)_";
      await this.post(initialText, i);
      this.streams.push(
        new SerializedMessageStream({
          apply: (content) => this.update(autoCloseMarkdown(content) || " ", i),
          throttleMs: this.throttleMs,
          onApplyError: this.onApplyError,
        }),
      );
    }
    // Dispatch slices.
    //
    // For continuation chunks (i > 0), if the boundary fell inside an open
    // markdown construct (e.g. inside a ```python block), prepend the
    // opener for that construct to the slice. Otherwise the second message
    // would begin with raw code that the platform renders as plain text.
    // The closer at the end of the chunk is added by `autoCloseMarkdown`,
    // which runs per chunk in the apply callback above.
    for (let i = 0; i < chunkCount; i++) {
      const start = i === 0 ? 0 : this.boundaries[i - 1]!;
      const end = i < this.boundaries.length ? this.boundaries[i]! : this.buffer.length;
      let slice = this.buffer.slice(start, end);
      if (i > 0) {
        const ctx = detectOpenContext(this.buffer.slice(0, start));
        const opener = renderContextOpener(ctx);
        if (opener) slice = opener + slice;
      }
      this.streams[i]!.enqueue(slice);
    }
  }
}
